import type { MetricRenderAdapter } from "@/metrics/runtime-adapters"
import type { RunOutlier, RunOutlierMetricSample } from "@/runs"

import type { RunRowLike } from "@/types/RunRow"

import {
  buildOutlierEvidence,
  type OutlierEvidenceBySimulationId,
} from "./buildOutlierEvidence"

interface OutlierMetricOption {
  id: string
  label: string
}

interface SelectedOutliers {
  selectedMetricIds: string[] | null
  availableMetricOptions: OutlierMetricOption[]
  hasExplicitNoSelection: boolean
  hasLowSampleWarning: boolean
  hasNoUsableMetricData: boolean
  outlierEvidenceBySimulationId: OutlierEvidenceBySimulationId
  qualifyingMetricIdsBySimulationId: Map<string, Set<string>>
  qualifyingSimulationOutputIds: Set<string>
}

const MIN_RELIABLE_OUTLIER_SAMPLE_SIZE = 12

function getMetricVersionKey(
  value: Pick<
    RunOutlier | RunOutlierMetricSample,
    "metric_id" | "metric_version_id" | "metric_version_number"
  >,
): string {
  return JSON.stringify([
    value.metric_id,
    value.metric_version_id ?? null,
    value.metric_version_number ?? null,
  ])
}

export function shapeSelectedOutliers({
  metricIdAliases = {},
  runRows = [],
  sourceRunRows,
  metricRenderAdaptersById,
  metricSamples,
  outliers,
  selectedMetricIds,
}: {
  metricIdAliases?: Readonly<Record<string, string>>
  runRows?: readonly RunRowLike[]
  sourceRunRows?: readonly RunRowLike[]
  metricRenderAdaptersById: Record<string, MetricRenderAdapter>
  metricSamples: readonly RunOutlierMetricSample[]
  outliers: readonly RunOutlier[]
  selectedMetricIds: readonly string[] | null
}): SelectedOutliers {
  const displayMetricId = (id: string) => metricIdAliases[id] ?? id
  const labelsByMetricId = new Map(
    Object.values(metricRenderAdaptersById).map((metric) => [
      metric.id,
      metric.metric_display_name,
    ]),
  )
  const availableMetricIds = [
    ...new Set(
      metricSamples.map((sample) => displayMetricId(sample.metric_id)),
    ),
  ]
  const availableMetricIdSet = new Set(availableMetricIds)
  const effectiveSelectedMetricIds = new Set(
    (selectedMetricIds ?? availableMetricIds)
      .map(displayMetricId)
      .filter((id) => availableMetricIdSet.has(id)),
  )
  const selectedSamples = metricSamples.filter((sample) =>
    effectiveSelectedMetricIds.has(displayMetricId(sample.metric_id)),
  )
  const usableSamplesByVersion = new Map(
    selectedSamples
      .filter(
        (sample) =>
          sample.status === "analyzed" &&
          typeof sample.iqr === "number" &&
          sample.iqr > 0,
      )
      .map((sample) => [getMetricVersionKey(sample), sample]),
  )
  const rowsBySimulationId = new Map(
    runRows.map((row) => [row.simulation_output_id, row]),
  )
  const sourceRowsBySimulationId =
    sourceRunRows &&
    new Map(sourceRunRows.map((row) => [row.simulation_output_id, row]))
  const aliasedMetricIds = new Set([
    ...Object.keys(metricIdAliases),
    ...Object.values(metricIdAliases),
  ])
  const qualifyingOutliers = outliers.filter((outlier) => {
    const metricId = displayMetricId(outlier.metric_id)
    const displayedOutput = rowsBySimulationId.get(outlier.simulation_output_id)
      ?.metric_outputs?.[metricId]
    // A source warning must not describe a replacement output displayed in its place.
    const sourceOutputs = sourceRowsBySimulationId?.get(
      outlier.simulation_output_id,
    )?.metric_outputs
    let storedMetricId = displayedOutput?.metric_id
    if (sourceOutputs) {
      if (Object.hasOwn(sourceOutputs, metricId)) {
        storedMetricId = metricId
      } else if (Object.hasOwn(sourceOutputs, outlier.metric_id)) {
        storedMetricId = outlier.metric_id
      } else {
        storedMetricId = undefined
      }
    }
    const matchesDisplayedIdentity = storedMetricId
      ? storedMetricId === outlier.metric_id
      : !aliasedMetricIds.has(outlier.metric_id)
    return (
      outlier.severity === "extreme" &&
      effectiveSelectedMetricIds.has(metricId) &&
      usableSamplesByVersion.has(getMetricVersionKey(outlier)) &&
      matchesDisplayedIdentity
    )
  })
  const hasExplicitNoSelection =
    selectedMetricIds !== null && effectiveSelectedMetricIds.size === 0
  const qualifyingSimulationOutputIds = new Set<string>()
  const qualifyingMetricIdsBySimulationId = new Map<string, Set<string>>()
  for (const outlier of qualifyingOutliers) {
    qualifyingSimulationOutputIds.add(outlier.simulation_output_id)
    const metricIds = qualifyingMetricIdsBySimulationId.get(
      outlier.simulation_output_id,
    )
    if (metricIds) {
      metricIds.add(displayMetricId(outlier.metric_id))
    } else {
      qualifyingMetricIdsBySimulationId.set(
        outlier.simulation_output_id,
        new Set([displayMetricId(outlier.metric_id)]),
      )
    }
  }

  return {
    selectedMetricIds:
      selectedMetricIds === null ? null : [...effectiveSelectedMetricIds],
    availableMetricOptions: availableMetricIds
      .map((id) => ({ id, label: labelsByMetricId.get(id) ?? id }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    hasExplicitNoSelection,
    hasLowSampleWarning: selectedSamples.some(
      (sample) => sample.sample_size < MIN_RELIABLE_OUTLIER_SAMPLE_SIZE,
    ),
    hasNoUsableMetricData:
      !hasExplicitNoSelection && usableSamplesByVersion.size === 0,
    outlierEvidenceBySimulationId: buildOutlierEvidence(
      qualifyingOutliers,
      metricSamples,
      metricRenderAdaptersById,
      metricIdAliases,
    ),
    qualifyingMetricIdsBySimulationId,
    qualifyingSimulationOutputIds,
  }
}
